// Copyright (C) 2026 TANNER AG

import React from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { CloseIcon } from "../icons/MANIcons";
import type { QuickAccessLinksQuery } from "../../graphql";
import useAsset from "../../hooks/useasset";
import useTranslation from "../../hooks/usetranslation";
import Spinner from "../Spinner";

type Props = {
    open: boolean;
    onClose: () => void;
    links: QuickAccessLinksQuery["quickAccessLinks"];
    fetching: boolean;
    error: boolean;
};

const isExternal = (url: string) => /^https?:\/\//i.test(url);

const QuickAccessDialog: React.FC<Props> = ({ open, onClose, links, fetching, error }) => {
    const { t } = useTranslation();
    const { createImageUrl } = useAsset();
    const navigate = useNavigate();

    const handleClick = (url?: string) => {
        if (!url) return;
        if (isExternal(url)) {
            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            navigate(url);
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h3">{t("quick_access")}</Typography>
                    <IconButton onClick={onClose} aria-label={t("close")}>
                        <CloseIcon sx={{ color: "#5B7085", fontSize: 14 }} />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {fetching && <Spinner />}
                {!fetching && error && (
                    <Typography color="error" variant="body2">
                        {t("error")}
                    </Typography>
                )}
                {!fetching && !error && links.length > 0 && (
                    <List disablePadding>
                        {links.map(link => (
                                <ListItemButton
                                    key={link.id}
                                    disabled={!link.url}
                                    onClick={() => handleClick(link.url ?? undefined)}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            variant="square"
                                            src={link.icon ? createImageUrl(link.icon) : undefined}
                                            alt={link.title ?? ""}
                                            sx={{ bgcolor: "transparent", "& img": { objectFit: "contain" } }}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText primary={link.title} />
                                </ListItemButton>
                            )
                        )}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default QuickAccessDialog;
